import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { type UserRole } from '@/stores/authStore';
import { useEffectiveAccess } from '@/lib/effectiveAccess';
import { hasAnyPermission } from '@/lib/permissions';
import { MIJN_FEEDBACK_PAD } from '@/lib/landing';

// Role hierarchy: admin > manager > employee > customer.
// Must stay identical to the API's own hierarchy (apps/api/src/middleware/rbac.middleware.ts).
// Leave `manager` out here and a manager passes the API but is bounced back to the dashboard.
const roleHierarchy: Record<UserRole, number> = {
  customer: 1,
  employee: 2,
  manager: 3,
  admin: 4,
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  /**
   * Story 4.1 / AD-15: a second, independent demand next to the rank. Holding one of these names
   * is enough; holding none sends the visitor to the dashboard, exactly as a too-low rank does.
   * The hierarchy is untouched — no fourth role, and no code that turns a role into a right
   * (NFR11), so a route that needs both has to name both.
   */
  requiredAnyPermission?: readonly string[];
}

/**
 * Het scherm waar iemand met een ongewijzigd beginwachtwoord naartoe gaat. Als constante, omdat
 * het pad op twee plaatsen moet kloppen: hier en in de routelijst. Hernoemen zonder deze constante
 * breekt de bewaking stil.
 */
export const WACHTWOORD_WIJZIGEN_PAD = '/change-password';

/**
 * Wat een employee te zien krijgt als hij er niet in mag: een melding in plaats van een omleiding.
 * Een omleiding naar het dashboard stuurt hem via de bewaking hieronder terug naar Mijn feedback, en
 * dan is het een kringetje.
 */
function GeenToegang() {
  const { t } = useTranslation();
  return <p role="alert" data-testid="geen-toegang" className="p-6 text-[var(--foreground)]">{t('errors.forbidden')}</p>;
}

export function ProtectedRoute({ children, requiredRole = 'customer', requiredAnyPermission }: ProtectedRouteProps) {
  const { user, role, permissions } = useEffectiveAccess();
  const isAuthenticated = Boolean(user);
  const location = useLocation();

  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  // Wie zijn beginwachtwoord nog moet wijzigen, komt nergens anders. Zonder deze regel stuurde
  // alleen het inloggen zelf naar /change-password en hield niets hem daar: wie daarna /dashboard
  // intypte, kwam er gewoon in (gemeten 26-08-2026). Het wijzigscherm zelf moet er wél doorheen,
  // anders verwijst het naar zichzelf.
  if (!user.hasChangedPassword && location.pathname !== WACHTWOORD_WIJZIGEN_PAD) {
    return <Navigate to={WACHTWOORD_WIJZIGEN_PAD} replace />;
  }

  // CAP-6 (spec-mijn-feedback-weekrapport): een employee ziet alleen "Mijn feedback". De server geeft
  // hem op elke andere route 403; dit is de tegenhanger op het scherm, zodat hij niet op een pagina
  // belandt waar elk verzoek wordt geweigerd. Op één plek en voor elke route, niet per route: een
  // nieuwe route kan hem zo niet per ongeluk binnenlaten. `role` is de effectieve rol, dus een
  // beheerder die een klant bekijkt valt hier niet onder. Het wachtwoordscherm blijft bereikbaar,
  // anders komt iemand met een beginwachtwoord er nooit meer uit.
  if (role === 'employee' && location.pathname !== MIJN_FEEDBACK_PAD && location.pathname !== WACHTWOORD_WIJZIGEN_PAD) {
    return <Navigate to={MIJN_FEEDBACK_PAD} replace />;
  }

  // Hierarchy check: admin can access employee routes, employee can access customer routes.
  // Both ranks are read out first and an unknown one refuses. A bare `a < b` on two map lookups
  // fails OPEN: one `undefined` makes every comparison false, the rank demand quietly disappears
  // and only whatever else the route asks for is left. `auth-user.mapper.ts` normalises an unknown
  // role to `customer` today, but a guard must not lean on a file in another folder for that.
  const rang = role ? roleHierarchy[role] : undefined;
  const vereisteRang = roleHierarchy[requiredRole];
  if (rang === undefined || vereisteRang === undefined || rang < vereisteRang) {
    return role === 'employee' ? <GeenToegang /> : <Navigate to="/dashboard" replace />;
  }

  // The same refusal as a too-low rank, and deliberately so: two refusal behaviours in one portal
  // is worse than one redirect, so there is no 403 screen (AD-15). `permissions` is already empty
  // during a client simulation, so the rights left in the session cannot open this door.
  if (requiredAnyPermission && !hasAnyPermission(permissions, requiredAnyPermission)) {
    return role === 'employee' ? <GeenToegang /> : <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
